import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { verifyPasskey, isWebAuthnSupported } from "@/lib/webauthn";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Fingerprint, Lock, Shield, KeyRound, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

/**
 * Wraps sensitive pages (Vault, Legal Vault).
 * Unlocks if the user has EITHER a fresh passkey verification OR a fresh
 * vault-PIN verification within the BIOMETRIC_VALID window.
 *
 * If neither passkey nor PIN is set, renders children directly (graceful —
 * the gate is opt-in, configured from Settings → Security).
 */
export function BiometricGate({ children, label = "this section" }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [unlocked, setUnlocked] = useState(true);
    const [hasPasskey, setHasPasskey] = useState(false);
    const [hasPin, setHasPin] = useState(false);
    const [verifying, setVerifying] = useState(false);
    const [pinMode, setPinMode] = useState(false);
    const [pinValue, setPinValue] = useState("");

    const check = useCallback(async () => {
        try {
            const [pkRes, pinRes] = await Promise.all([
                api.get("/security/passkey/list"),
                api.get("/security/vault-pin/status"),
            ]);
            const pkItems = pkRes.data?.items || [];
            const pkVerifiedAt = pkRes.data?.verified_at;
            const minutes = pkRes.data?.valid_for_minutes ?? 10;
            const pinHas = !!pinRes.data?.has_pin;
            const pinVerifiedAt = pinRes.data?.verified_at;

            setHasPasskey(pkItems.length > 0);
            setHasPin(pinHas);

            if (pkItems.length === 0 && !pinHas) {
                setUnlocked(true);
                setLoading(false);
                return;
            }
            const fresh = (ts) => ts && Date.now() - new Date(ts).getTime() < minutes * 60 * 1000;
            const ok = (pkItems.length > 0 && fresh(pkVerifiedAt)) || (pinHas && fresh(pinVerifiedAt));
            setUnlocked(!!ok);
        } catch {
            setUnlocked(true); // fail-open so users aren't locked out by API errors
        } finally {
            setLoading(false);
        }
    }, []);
    useEffect(() => {
        const id = setTimeout(() => check(), 0);
        return () => clearTimeout(id);
    }, [check]);

    const onVerifyPasskey = async () => {
        setVerifying(true);
        try {
            await verifyPasskey();
            toast.success("Verified — unlocked");
            setUnlocked(true);
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Verification failed.");
        } finally {
            setVerifying(false);
        }
    };

    const onVerifyPin = async () => {
        if (pinValue.length < 4) { toast.error("Enter your PIN (4–6 digits)."); return; }
        setVerifying(true);
        try {
            await api.post("/security/vault-pin/verify", { pin: pinValue });
            toast.success("PIN verified — unlocked");
            setUnlocked(true);
            setPinValue("");
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Wrong PIN.");
        } finally {
            setVerifying(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen vt-pattern flex items-center justify-center">
                <div className="text-[#777]">Checking security…</div>
            </div>
        );
    }
    if (unlocked) return children;

    return (
        <div
            className="min-h-screen vt-pattern flex items-center justify-center px-5"
            data-testid="biometric-gate"
        >
            <div className="max-w-md w-full bg-white rounded-3xl border border-[#E5E5E5] shadow-[0_12px_40px_rgba(27,94,32,0.10)] p-8 sm:p-10 text-center">
                <div className="mx-auto h-16 w-16 rounded-full bg-[#1B5E20]/10 flex items-center justify-center">
                    <Lock className="h-8 w-8 text-[#1B5E20]" />
                </div>
                <h1 className="mt-5 text-[22px] font-bold text-[#1F1F1F]">
                    Verification required
                </h1>
                <p className="mt-2 text-[14.5px] text-[#555]">
                    For your safety, unlock {label} with{" "}
                    {hasPasskey && hasPin ? "biometric or PIN" : hasPasskey ? "biometric" : "your PIN"}.
                </p>

                {pinMode && hasPin ? (
                    <div className="mt-6 space-y-3 text-left">
                        <label className="text-[13px] text-[#555]">Enter Vault PIN</label>
                        <Input
                            type="password"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            maxLength={6}
                            value={pinValue}
                            onChange={(e) => setPinValue(e.target.value.replace(/\D/g, "").slice(0, 6))}
                            data-testid="biometric-pin-input"
                            className="h-12 tracking-[0.5em] text-center font-mono text-lg"
                            placeholder="••••"
                            autoFocus
                        />
                        <Button
                            data-testid="biometric-pin-submit"
                            onClick={onVerifyPin}
                            disabled={verifying || pinValue.length < 4}
                            className="w-full h-12 rounded-xl bg-[#1B5E20] hover:bg-[#154d1a] text-white"
                        >
                            {verifying ? "Verifying…" : "Unlock with PIN"}
                        </Button>
                        <button
                            type="button"
                            onClick={() => navigate("/settings?tab=security")}
                            data-testid="biometric-pin-forgot"
                            className="w-full text-[13px] text-[#F57C00] hover:underline inline-flex items-center justify-center gap-1"
                        >
                            <MessageSquare className="h-3.5 w-3.5" /> Forgot PIN? Reset via OTP
                        </button>
                        {hasPasskey && (
                            <button
                                type="button"
                                onClick={() => { setPinMode(false); setPinValue(""); }}
                                data-testid="biometric-switch-passkey"
                                className="w-full text-[13px] text-[#1B5E20] hover:underline"
                            >
                                Use biometric instead
                            </button>
                        )}
                    </div>
                ) : (
                    <div className="mt-6 space-y-2">
                        {hasPasskey && (
                            <>
                                {!isWebAuthnSupported() && (
                                    <p className="mt-3 text-[12.5px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                                        This browser does not support passkeys.
                                    </p>
                                )}
                                <Button
                                    data-testid="biometric-unlock-btn"
                                    onClick={onVerifyPasskey}
                                    disabled={!isWebAuthnSupported() || verifying}
                                    className="w-full h-12 rounded-xl bg-[#1B5E20] hover:bg-[#154d1a] text-white"
                                >
                                    <Fingerprint className="h-5 w-5 mr-2" />
                                    {verifying ? "Verifying…" : "Unlock with biometric"}
                                </Button>
                            </>
                        )}
                        {hasPin && (
                            <Button
                                data-testid="biometric-use-pin"
                                onClick={() => setPinMode(true)}
                                variant={hasPasskey ? "outline" : "default"}
                                className={`w-full h-12 rounded-xl ${hasPasskey ? "border-[#1B5E20]/30 text-[#1B5E20]" : "bg-[#1B5E20] hover:bg-[#154d1a] text-white"}`}
                            >
                                <KeyRound className="h-5 w-5 mr-2" />
                                {hasPasskey ? "Or use Vault PIN" : "Unlock with PIN"}
                            </Button>
                        )}
                        <Button
                            variant="ghost"
                            onClick={() => navigate("/dashboard")}
                            data-testid="biometric-cancel-btn"
                            className="w-full h-11 text-[#666]"
                        >
                            Cancel
                        </Button>
                    </div>
                )}

                <p className="mt-5 text-[11.5px] text-[#999] flex items-center justify-center gap-1">
                    <Shield className="h-3 w-3" /> 10-minute window after verification
                </p>
            </div>
        </div>
    );
}

export default BiometricGate;
